const PLACEHOLDER = /^__.*__$/;

/**
 * The apex origin, which the server writes into every shell
 * (`<meta property="public-origin">`, src/indexHtml.ts). Empty when absent —
 * unit tests, the unfilled template — so links stay relative there.
 */
function publicOrigin(): string {
  const content =
    document
      .querySelector('meta[property="public-origin"]')
      ?.getAttribute('content') ?? '';
  return PLACEHOLDER.test(content) ? '' : content;
}

/**
 * Absolute URL of a page that lives on the apex only (accessibility
 * statement, later privacy and terms): one canonical URL per page, linked
 * the same from every area (ADR 0016 §7). Hosts come from deploy config via
 * the server, never hard-coded.
 */
export function publicUrl(path: string): string {
  return `${publicOrigin()}${path}`;
}

/** True when this document is the apex (or no origin is known): in-app navigation reaches the page. */
export function isOnPublicOrigin(): boolean {
  const origin = publicOrigin();
  return origin === '' || origin === window.location.origin;
}

/**
 * Origin of the hub or admin area, which the server writes into every shell
 * (`<meta property="hub-origin">` / `admin-origin`). Null when absent (unit
 * tests, the unfilled template): a relative link would point at the current
 * area, so callers leave the link out instead.
 */
export function areaOrigin(area: 'hub' | 'admin'): string | null {
  const content =
    document
      .querySelector(`meta[property="${area}-origin"]`)
      ?.getAttribute('content') ?? '';
  return content === '' || PLACEHOLDER.test(content) ? null : content;
}

/**
 * Origin of the Grafana vhost, which sits on the `grafana.` subdomain of the
 * apex (docs/architecture/metrics.md -> Public access). Null when the apex is unknown or is localhost,
 * where no such vhost exists, so callers leave the link out.
 */
export function grafanaOrigin(): string | null {
  const origin = publicOrigin();
  if (origin === '') return null;
  const { protocol, host, hostname } = new URL(origin);
  if (hostname === 'localhost' || hostname.endsWith('.localhost')) return null;
  return `${protocol}//grafana.${host}`;
}
